<template>
  <div class="prepared-notifications-table">
    <div class="d-flex align-center justify-space-between mb-4">
      <div class="text-h6">Připravené notifikace</div>
      <ButtonElement
        v-if="selectedIds.length > 0"
        :text="`Odeslat vybrané (${selectedIds.length})`"
        icon="send"
        :loading="bulkSendLoading"
        @click="sendSelected"
      />
    </div>

    <DataTable
      :loading="loading"
      :headers="headers"
      :items="items"
      :metadata="metadata"
      @updateFooterOptions="updateFooterOptions"
    >
      <template #item.select="{ item }">
        <v-checkbox
          v-model="selectedIds"
          :value="item.id"
          hide-details
          class="mt-0 pt-0"
        />
      </template>

      <template #item.name="{ item }">
        <span
          style="
            max-width: 220px;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
        >
          {{ item.name || "—" }}
        </span>
      </template>

      <template #item.title="{ item }">
        <span
          style="
            max-width: 220px;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
        >
          {{ item.title }}
        </span>
      </template>

      <template #item.recipients="{ item }">
        {{ getRecipientsLabel(item) }}
      </template>

      <template #item.createdAt="{ item }">
        {{ getCzechDate(item.createdAt) }}
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex align-center justify-end">
          <v-btn icon @click.stop="openEdit(item)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon @click.stop="openRecipients(item)">
            <v-icon>mdi-account-multiple-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            color="primary"
            :loading="item._sendLoading"
            @click.stop="sendOne(item)"
          >
            <v-icon>mdi-send-outline</v-icon>
          </v-btn>
          <v-btn icon color="error" @click.stop="confirmDelete(item)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </template>
    </DataTable>

    <!-- Detail dialog -->
    <AppDialog
      :show-dialog="!!detailItem"
      title="Detail notifikace"
      hide-actions
      @closeDialog="detailItem = null"
    >
      <div v-if="detailItem">
        <div class="mb-3">
          <small class="grey--text">Název</small>
          <div style="word-break: break-word">{{ detailItem.name || "—" }}</div>
        </div>
        <div class="mb-3">
          <small class="grey--text">Nadpis</small>
          <div style="word-break: break-word">{{ detailItem.title }}</div>
        </div>
        <div class="mb-3">
          <small class="grey--text">Text</small>
          <div style="word-break: break-word; white-space: pre-wrap">
            {{ detailItem.body || "—" }}
          </div>
        </div>
        <div v-if="detailItem.actionLink" class="mb-3">
          <small class="grey--text">Odkaz</small>
          <div style="word-break: break-word">{{ detailItem.actionLink }}</div>
        </div>
        <div class="mb-3">
          <small class="grey--text">Příjemci</small>
          <div>{{ getRecipientsLabel(detailItem) }}</div>
        </div>
        <div class="mb-3">
          <small class="grey--text">Vytvořeno</small>
          <div>{{ getCzechDate(detailItem.createdAt) }}</div>
        </div>
      </div>
    </AppDialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="450" persistent>
      <div class="white pa-8">
        <div class="text-h6 mb-4">Smazat notifikaci</div>
        <p class="grey--text text--darken-2">
          Opravdu chcete smazat tuto notifikaci?
        </p>
        <div class="d-flex justify-end">
          <ButtonElement
            secondary
            text="Zrušit"
            class="mr-2"
            @click="showDeleteDialog = false"
          />
          <ButtonElement
            text="Smazat"
            color="error"
            :loading="deleteLoading"
            @click="deleteItem"
          />
        </div>
      </div>
    </v-dialog>

    <!-- Edit dialog -->
    <AppDialog
      :show-dialog="!!editItem"
      title="Upravit notifikaci"
      hide-actions
      @closeDialog="editItem = null"
    >
      <NotificationForm
        v-if="editItem"
        :edit-id="editItem.id"
        :initial-data="editItem"
        @updated="onEditDone"
        @close="editItem = null"
      />
    </AppDialog>

    <!-- Recipients dialog -->
    <v-dialog v-model="showRecipientsDialog" max-width="750">
      <div class="white pa-6">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="text-h6">Příjemci notifikace</div>
          <v-btn icon @click="showRecipientsDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>
        <div v-if="recipientsItem" class="text-body-2 grey--text mb-3">
          {{ recipientsItem.name || recipientsItem.title }}
        </div>
        <v-progress-linear
          v-if="recipientsLoading"
          indeterminate
          class="mb-4"
        />
        <v-simple-table v-else dense>
          <template #default>
            <thead>
              <tr>
                <th>Email</th>
                <th>Jméno</th>
                <th class="text-center">Push notifikace</th>
                <th class="text-center">Notifikační centrum</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in recipientsData" :key="r.id">
                <td>{{ r.email }}</td>
                <td>{{ r.fullName || "—" }}</td>
                <td class="text-center">
                  <v-tooltip
                    bottom
                    :disabled="
                      isDeliverable(r.undeliverableFcmNotificationReason)
                    "
                  >
                    <template #activator="{ on }">
                      <v-icon
                        small
                        :color="
                          isDeliverable(r.undeliverableFcmNotificationReason)
                            ? 'success'
                            : 'warning'
                        "
                        v-on="on"
                      >
                        {{
                          isDeliverable(r.undeliverableFcmNotificationReason)
                            ? "mdi-check"
                            : "mdi-alert-outline"
                        }}
                      </v-icon>
                    </template>
                    <span>{{
                      deliverabilityLabel(r.undeliverableFcmNotificationReason)
                    }}</span>
                  </v-tooltip>
                </td>
                <td class="text-center">
                  <v-tooltip
                    bottom
                    :disabled="
                      isDeliverable(r.undeliverableAppNotificationReason)
                    "
                  >
                    <template #activator="{ on }">
                      <v-icon
                        small
                        :color="
                          isDeliverable(r.undeliverableAppNotificationReason)
                            ? 'success'
                            : 'warning'
                        "
                        v-on="on"
                      >
                        {{
                          isDeliverable(r.undeliverableAppNotificationReason)
                            ? "mdi-check"
                            : "mdi-alert-outline"
                        }}
                      </v-icon>
                    </template>
                    <span>{{
                      deliverabilityLabel(r.undeliverableAppNotificationReason)
                    }}</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!recipientsLoading && recipientsData.length === 0">
                <td colspan="4" class="text-center grey--text py-4">
                  Žádní příjemci
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div class="d-flex align-center justify-space-between mt-3">
          <span class="text-caption grey--text"
            >Celkem: {{ recipientsMeta.totalRecords }}</span
          >
          <div class="d-flex align-center">
            <v-btn
              icon
              small
              :disabled="recipientsParams.skip === 0 || recipientsLoading"
              @click="changeRecipientsPage(-1)"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-caption mx-2">
              {{ recipientsParams.skip / recipientsParams.take + 1 }} /
              {{
                Math.max(
                  1,
                  Math.ceil(
                    recipientsMeta.totalRecords / recipientsParams.take,
                  ),
                )
              }}
            </span>
            <v-btn
              icon
              small
              :disabled="
                recipientsParams.skip + recipientsParams.take >=
                  recipientsMeta.totalRecords || recipientsLoading
              "
              @click="changeRecipientsPage(1)"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import DataTable from "../elements/DataTable.vue";
import AppDialog from "../elements/AppDialog.vue";
import ButtonElement from "../elements/ButtonElement.vue";
import NotificationForm from "../forms/NotificationForm.vue";
import useNotifications from "../../use/useNotifications";
import useFormatDate from "../../use/formatDate";

export default {
  name: "PreparedNotificationsTable",

  components: { DataTable, AppDialog, ButtonElement, NotificationForm },

  setup() {
    const {
      getPreparedNotifications,
      sendPreparedNotification,
      deletePreparedNotification,
      updatePreparedNotification,
      getPreparedNotificationRecipients,
      getPushStatus,
    } = useNotifications();
    const { getCzechDate } = useFormatDate();
    return {
      getPreparedNotifications,
      sendPreparedNotification,
      deletePreparedNotification,
      updatePreparedNotification,
      getPreparedNotificationRecipients,
      getPushStatus,
      getCzechDate,
    };
  },

  data() {
    return {
      loading: false,
      bulkSendLoading: false,
      deleteLoading: false,
      showDeleteDialog: false,
      itemToDelete: null,
      detailItem: null,
      editItem: null,
      showRecipientsDialog: false,
      recipientsItem: null,
      recipientsLoading: false,
      recipientsData: [],
      recipientsMeta: { totalRecords: 0 },
      recipientsParams: { skip: 0, take: 15 },
      selectedIds: [],
      items: [],
      metadata: { totalRecords: 0 },
      tableParams: { skip: 0, take: 10 },
      headers: [
        { text: "", value: "select", sortable: false, width: "48px" },
        { text: "Název", value: "name", sortable: false },
        { text: "Nadpis", value: "title", sortable: false },
        { text: "Příjemci", value: "recipients", sortable: false },
        { text: "Vytvořeno", value: "createdAt", sortable: false },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
      recipientSourceLabels: {
        ExplicitIds: "Výběr pacientů",
        ExcelUpload: "Excel soubor",
        CustomQuery: "Skupina",
      },
      undeliverableLabels: {
        notActivated: "Neaktivovaný účet",
        notMobileAppUser: "Nemá mobilní aplikaci",
        locked: "Zamčený účet",
        invalidRole: "Neplatná role",
        noUsableFcmToken: "Žádný platný FCM token",
      },
    };
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const result = await this.getPreparedNotifications({
          skip: this.tableParams.skip,
          take: this.tableParams.take,
          status: "prepared",
        });
        if (result) {
          this.items = (result.preparedNotifications || []).map((n) => ({
            ...n,
            _sendLoading: false,
          }));
          this.metadata = result.metadata || {
            totalRecords: this.items.length,
          };
        }
      } finally {
        this.loading = false;
      }
    },

    async updateFooterOptions(event) {
      this.tableParams.skip = event.page * event.itemsPerPage;
      this.tableParams.take = event.itemsPerPage;
      await this.fetchData();
    },

    getRecipientsLabel(item) {
      const type = item.recipientSourceType || "";
      const key = Object.keys(this.recipientSourceLabels).find(
        (k) => k.toLowerCase() === type.toLowerCase(),
      );
      return (key && this.recipientSourceLabels[key]) || type || "—";
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },

    async deleteItem() {
      if (!this.itemToDelete) return;
      this.deleteLoading = true;
      const idToDelete = this.itemToDelete.id;
      try {
        const response = await this.deletePreparedNotification(idToDelete);
        if (response !== undefined) {
          this.showDeleteDialog = false;
          this.itemToDelete = null;
          this.selectedIds = this.selectedIds.filter((id) => id !== idToDelete);
          await this.fetchData();
        }
      } finally {
        this.deleteLoading = false;
      }
    },

    async sendOne(item) {
      item._sendLoading = true;
      try {
        const response = await this.sendPreparedNotification(item.id);
        if (response !== undefined && response !== null) {
          if (response.operationId) {
            await this.waitForPushComplete(response.operationId);
          }
          this.$emit("sent");
          await this.fetchData();
        }
      } finally {
        item._sendLoading = false;
      }
    },

    async sendSelected() {
      if (this.selectedIds.length === 0) return;
      this.bulkSendLoading = true;
      try {
        let lastOperationId = null;
        for (const id of this.selectedIds) {
          const response = await this.sendPreparedNotification(id);
          if (response?.operationId) lastOperationId = response.operationId;
        }
        if (lastOperationId) await this.waitForPushComplete(lastOperationId);
        this.selectedIds = [];
        this.$emit("sent");
        await this.fetchData();
      } finally {
        this.bulkSendLoading = false;
      }
    },

    async waitForPushComplete(operationId) {
      for (let i = 0; i < 20; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const result = await this.getPushStatus(operationId);
        if (!result) return;
        const s = (result.status || "").toLowerCase();
        if (s === "completed" || s === "failed") return;
      }
    },

    openEdit(item) {
      this.editItem = item;
    },

    async onEditDone() {
      this.editItem = null;
      await this.fetchData();
    },

    async openRecipients(item) {
      this.recipientsItem = item;
      this.recipientsParams.skip = 0;
      this.recipientsData = [];
      this.recipientsMeta = { totalRecords: 0 };
      this.showRecipientsDialog = true;
      await this.fetchRecipients();
    },

    async fetchRecipients() {
      if (!this.recipientsItem) return;
      this.recipientsLoading = true;
      try {
        const result = await this.getPreparedNotificationRecipients(
          this.recipientsItem.id,
          {
            skip: this.recipientsParams.skip,
            take: this.recipientsParams.take,
          },
        );
        if (result) {
          this.recipientsData = result.recipients || [];
          this.recipientsMeta = result.metadata || { totalRecords: 0 };
        }
      } finally {
        this.recipientsLoading = false;
      }
    },

    async changeRecipientsPage(direction) {
      this.recipientsParams.skip = Math.max(
        0,
        this.recipientsParams.skip + direction * this.recipientsParams.take,
      );
      await this.fetchRecipients();
    },

    isDeliverable(reason) {
      return !reason || reason.toLowerCase() === "none";
    },

    deliverabilityLabel(reason) {
      if (!reason) return "";
      return this.undeliverableLabels[reason] || reason;
    },
  },
};
</script>
